import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { buySubmissionFormEventDataSchema } from "@/core";
import { useBuyForm } from "@/hooks/forms/useBuyForm";
import { MilesProgram } from "@/lib/miles-calculations";
import { TransactionTypes, useDashboardStore } from "@/stores";
import { Check } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { z } from "zod";
import { FloatingCTAButton } from "../dashboard";
import { Card } from "../ui/card";
import { Input } from "../ui/input";
import { CheckoutButton } from "./CheckoutButton";
import { TermsAndConditions } from "./TermsAndConditions";

export function DetailsBuyForm({
  onContinueClick,
  programs,
  currentProgramIndex,
  isFormSubmitting,
}: {
  programs: MilesProgram[];
  onContinueClick: () => void;
  currentProgramIndex: number;
  isFormSubmitting: boolean;
}) {
  const updateProgram = useDashboardStore((s) => s.updateProgram);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [isTermsAndConditionsOpen, setIsTermsAndConditionsOpen] =
    useState(false);
  const formRef = useRef<HTMLDivElement>(null);

  const currentProgram = programs[currentProgramIndex];
  const form = useBuyForm();
  const {
    watch,
    formState: { touchedFields, errors },
  } = form;

  useEffect(() => {
    const subscription = watch((value) => {
      updateProgram({
        accountDetails: value as Omit<
          z.infer<typeof buySubmissionFormEventDataSchema.shape.data>,
          "miles" | "averageRate" | "totalPayout"
        >,
      });
    });
    return () => subscription.unsubscribe();
  }, [
    watch,
    updateProgram,
    programs,
    currentProgramIndex,
    currentProgram?.programId,
  ]);

  const handleFormSubmit = () => {
    // Always close terms and conditions when submit is attempted
    //setIsTermsAndConditionsOpen(false);
  };

  const handleSubmit = (
    data: Omit<
      z.infer<typeof buySubmissionFormEventDataSchema.shape.data>,
      "miles" | "averageRate" | "totalPayout"
    >,
  ) => {
    const program = programs[currentProgramIndex];
    if (program) {
      updateProgram({
        accountDetails: {
          ...program.accountDetails,
          ...data,
        },
      });
    }

    onContinueClick();
    form.reset();
    setAgreedToTerms(false);
  };

  return (
    <Form {...form}>
      <FloatingCTAButton offersRef={formRef} />
      <Card
        ref={formRef}
        className="w-full order-2 xl:order-1 bg-white rounded-md z-10 shadow-lg h-fit p-0"
      >
        <form
          onSubmit={(e) => {
            handleFormSubmit();
            form.handleSubmit(handleSubmit)(e);
          }}
        >
          <div className="p-6">
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="firstName"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <div className="relative">
                        <Input
                          placeholder="First Name *"
                          className="text-sm border-gray-300 rounded-sm focus:border-blue-500 focus:ring-blue-500"
                          {...field}
                        />
                        {!errors.firstName &&
                          field.value &&
                          touchedFields.firstName && (
                            <Check className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-green-500" />
                          )}
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="lastName"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <div className="relative">
                        <Input
                          placeholder="Last Name *"
                          className="text-sm border-gray-300 rounded-sm focus:border-blue-500 focus:ring-blue-500"
                          {...field}
                        />
                        {!errors.lastName &&
                          field.value &&
                          touchedFields.lastName && (
                            <Check className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-green-500" />
                          )}
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="accountEmail"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <div className="relative">
                        <Input
                          placeholder="Email On Account *"
                          className="text-sm border-gray-300 rounded-sm focus:border-blue-500 focus:ring-blue-500"
                          {...field}
                        />
                        {!errors.accountEmail &&
                          field.value &&
                          touchedFields.accountEmail && (
                            <Check className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-green-500" />
                          )}
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="frequentFlyerNumber"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <div className="relative">
                        <Input
                          placeholder="Frequent flyer number *"
                          className="text-sm border-gray-300 rounded-sm focus:border-blue-500 focus:ring-blue-500"
                          {...field}
                        />
                        {!errors.frequentFlyerNumber &&
                          field.value &&
                          touchedFields.frequentFlyerNumber && (
                            <Check className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-green-500" />
                          )}
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="col-span-1 xl:col-span-2">
                <FormField
                  control={form.control}
                  name="receivedMilesBefore"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <div className="relative">
                          <Select
                            value={field.value}
                            onValueChange={(value) => {
                              field.onChange(value);
                              field.onBlur();
                            }}
                          >
                            <SelectTrigger className="text-sm border-gray-300 rounded-sm focus:border-blue-500 focus:ring-blue-500 w-full">
                              <SelectValue placeholder="Did you ever receive points before *" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="3months">3 months</SelectItem>
                              <SelectItem value="6months">6 months</SelectItem>
                              <SelectItem value="1year">1 year</SelectItem>
                              <SelectItem value="never">Never</SelectItem>
                            </SelectContent>
                          </Select>
                          {!form.formState.errors.receivedMilesBefore &&
                            field.value &&
                            form.formState.touchedFields
                              .receivedMilesBefore && (
                              <Check className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-green-500" />
                            )}
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            <div className="mt-6 space-y-3">
              <FormField
                control={form.control}
                name="notes"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Notes</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Please provide any additional comments associated to this request"
                        className="rounded-sm"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="text-sm flex flex-col gap-6 py-6">
                <p>
                  Transfers are completed from 8am to 5pm EST Sunday - Friday
                </p>
                <p>
                  Any transfer request received after 5pm will be completed the
                  next day (except Saturday)
                </p>

                <TermsAndConditions
                  isOpen={isTermsAndConditionsOpen}
                  setIsOpen={setIsTermsAndConditionsOpen}
                  agreedToTerms={agreedToTerms}
                  setAgreedToTerms={setAgreedToTerms}
                  formRef={formRef}
                />

                <CheckoutButton
                  transactionType={TransactionTypes.BUY}
                  type="submit"
                  isFormSubmitting={isFormSubmitting}
                  disabled={!agreedToTerms || !form.formState.isValid}
                />
              </div>
            </div>
          </div>
        </form>
      </Card>
    </Form>
  );
}
